import { useState, useEffect, useMemo, useRef, lazy, Suspense, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useCurrencyContext } from '../contexts/CurrencyContext';
import { logger } from '../services/logger';
import { Icons } from '../components/Icons';
import { PortfolioTable } from '../components/portfolio/PortfolioTable';
import { DEFAULT_FILTERS, type FilterConfig } from '../components/portfolio/PortfolioFilters';
import { SavedFilterViews } from '../components/portfolio/SavedFilterViews';
import { usePortfolioSavedFilterViews } from '../hooks/usePortfolioSavedFilterViews';
import { PortfolioToolbarBar } from '../components/portfolio/PortfolioToolbarBar';
import { PortfolioSelectorBar } from '../components/portfolio/PortfolioSelectorBar';
import { PositionModal } from '../components/modals/PositionModal';
import { SwapModal } from '../components/modals/SwapModal';
import { PortfolioModal } from '../components/modals/PortfolioModal';
import { TaxExportModal } from '../components/export/TaxExportModal';
import { InvestmentSimulator } from '../components/modals/InvestmentSimulator';
import { InvestmentAdvisorChat } from '../components/advisor';
import { RiskAnalysis } from '../components/portfolio/RiskAnalysis';
import { SnapshotManager } from '../components/portfolio/SnapshotManager';
import { CSVImportModal } from '../components/CSVImportModal';
import { PWAInstallPrompt } from '../components/pwa/PWAInstallPrompt';
import { TokenDiscoveryPopup } from '../components/portfolio/TokenDiscoveryPopup';
import { HbarPortfolioWidget } from '../components/hedera/HbarPortfolioWidget';
import { ChartSkeleton } from '../components/ui/LoadingSkeleton';
import { DataPageHeader } from '../components/ui/DataPageHeader';
import { KeyboardShortcutsModal } from '../components/ui/KeyboardShortcutsModal';
import { AddTokenModal } from '../components/portfolio/AddTokenModal';

// Lazy load heavy chart components for better initial page load.
// V2.G7.5: DrawdownChart, EntryPointsHeatmap and HoldingDurationAnalysis
// are no longer rendered here (moved to analytics/Performance,
// analytics/Trading and analytics/Positions canonical pages).
const SpreadChart = lazy(() => import('../components/SpreadChart').then(m => ({ default: m.SpreadChart })));
const TokenPriceChart = lazy(() => import('../components/visualizations/TokenPriceChart').then(m => ({ default: m.TokenPriceChart })));
const PortfolioUniverse3D = lazy(() => import('../components/universe3D/PortfolioUniverse3D').then(m => ({ default: m.PortfolioUniverse3D })));
const DashboardHeader = lazy(() => import('../components/dashboard/DashboardHeader').then(m => ({ default: m.DashboardHeader })));
// V2.G7: opt-in 3-line redesign behind a localStorage feature flag.
// `enable_dashboard_header_v2 = true` swaps in DashboardHeaderV2.
const DashboardHeaderV2 = lazy(() => import('../components/dashboard/DashboardHeaderV2').then(m => ({ default: m.DashboardHeaderV2 })));
// V2.H: opt-in "Glass Hero" redesign behind a localStorage feature flag.
// `enable_dashboard_header_v3 = true` swaps in DashboardHeaderV3.
// V3 takes precedence over V2 when both flags are set.
const DashboardHeaderV3 = lazy(() => import('../components/dashboard/DashboardHeaderV3').then(m => ({ default: m.DashboardHeaderV3 })));
import { DashboardHeaderSkeleton } from '../components/dashboard/DashboardHeaderSkeleton';
// PortfolioCompositionDonut, DrawdownChart, EntryPointsHeatmap and
// HoldingDurationAnalysis are no longer used here (V2.G7.5 — they live
// in analytics/* canonical pages). Imports removed.
import { CollapsibleSection } from '../components/CollapsibleSection';
import { PortfolioDashboardSkeleton } from '../components/SkeletonLoader';
import { DashboardGrid, EditModeToggle, LayoutPresetSelector, resetDashboardLayout, type WidgetConfig } from '../components/dashboard';
import {
  PORTFOLIO_LAYOUT_PRESETS,
  type PortfolioLayoutPresetId,
} from '../data/portfolioLayoutPresets';
import { useToast } from '../components/Toast';
import { useConfirmation } from '../hooks/useConfirmation';
import { ConfirmationModal } from '../components/ui/ConfirmationModal';
import { useDarkMode } from '../hooks/useDarkMode';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';
import { useSwipeGestures } from '../hooks/useSwipeGestures';
import { useNewItemHighlight } from '../hooks/useNewItemHighlight';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { useCoinsList, useCoinPrices } from '../hooks/queries/useCoinGecko';
import { CalculatorFactory } from '../services/calculator';
import { StorageService } from '../services/storage';
import { useCelebrations } from '../components/micro-interactions/CelebrationProvider';
import { useOptionalFeatureDiscovery } from '../contexts/FeatureDiscoveryContext';
import { BTCPriceHistoryService } from '../services/btcPriceHistory';
import { usePortfolio } from '../hooks/usePortfolio';
import { usePositions } from '../hooks/usePositions';
import { useModalManager } from '../hooks/useModalManager';

// Modal keys for the manager — string-literal union narrows the API.
// Pass 4 Commit 2 migrates the 9 useState<boolean> flags to this set
// in 3 sub-batches.
type PortfolioModalKey =
  | 'portfolio' | 'taxExport' | 'snapshot' // Batch A
  | 'csvImport' | 'investmentSimulator' // Batch B (createAlert removed in V2.B1)
  | 'position' | 'swap' | 'addToken'; // Batch C
import { DEMO_PORTFOLIO } from '../data/demoPortfolio';
import { PortfolioManager, type Portfolio as LegacyPortfolio } from '../services/portfolioManager';
import type { UserMode } from '../hooks/useUserMode';
import { exportPortfolioCSV } from '../services/export';
import { exportBackup, importBackup, getImportSummary } from '../services/backupService';
import { alertService } from '../services/alertService';
import { userPreferencesService } from '../services/userPreferencesService';
import { useAuthStore } from '../store/authStore';
import { fmt } from '../utils/formatters';
import { calculateOriginalCostBasis, calculateCostBasisPerUnit, generateSwapChainId } from '../utils/costBasisTracking';
import { FALLBACK_COINS } from '../data/fallbackCoins';
import type { Token, Prices, FIFOResults, Position, CalculationMethod } from '../types';
import type { PricesCacheInfo } from '../types/portfolio';

/**
 * Portfolio Dashboard - Main portfolio management interface
 *
 * Features:
 * - Real-time price data from CoinGecko API
 * - Add/edit/delete positions
 * - FIFO/LIFO/Average Cost calculations
 * - Portfolio metrics and analytics
 * - Export to CSV/JSON
 * - LocalStorage persistence
 */

interface PortfolioDashboardProps {
  calculationMethod?: CalculationMethod;
  userMode?: UserMode;
  onNavigate?: (pageId: string) => void;
}

// Widget configurations for customizable dashboard grid
// V2.G7.5: 'heatmap' / 'drawdown' / 'holding' removed (charts moved to
// analytics/Trading, /Performance, /Positions canonical pages).
// Saved layouts referencing the removed IDs degrade gracefully — the
// addWidget helper silently skips unknown IDs.
const ANALYTICS_WIDGETS: WidgetConfig[] = [
  { id: 'risk', title: 'Risk Analysis', icon: Icons.Shield, defaultCols: 6 },
  { id: 'price-chart', title: 'Price Chart', icon: Icons.LineChart, defaultCols: 12 },
  { id: 'spread', title: 'Performance vs BTC', icon: Icons.Activity, defaultCols: 12 },
];

const DASHBOARD_STORAGE_KEY = 'portfolio_dashboard_layout';
const DASHBOARD_PRESET_STORAGE_KEY = 'portfolio_dashboard_preset';

export function PortfolioDashboard({ calculationMethod = 'FIFO', userMode = 'simple', onNavigate }: PortfolioDashboardProps = {}) {
  // Translation hooks
  const { t } = useTranslation();
  const { language } = useLanguage(); // Force re-render on language change
  const { currency, currencyInfo } = useCurrencyContext();
  const celebrations = useCelebrations();
  const featureDiscovery = useOptionalFeatureDiscovery();
  const { user, isAuthenticated } = useAuthStore();

  // 🔄 HYBRID MODE: Use Portfolio & Positions Hooks (localStorage OR Supabase)
  const portfolioHook = usePortfolio();
  const positionsHook = usePositions(portfolioHook.activePortfolio?.id);

  // Modal manager — replaces individual `useState<boolean>` flags.
  // Migration is staged (cf. docs/v2/PORTFOLIO_MODALS_HOST_DESIGN.md):
  // Batch A landed `portfolio | taxExport | snapshot` here; subsequent
  // batches will migrate the remaining 6 flags.
  const modals = useModalManager<PortfolioModalKey>();

  // Multi-portfolio states
  const [activePortfolio, setActivePortfolio] = useState<LegacyPortfolio | null>(null);

  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(portfolioHook.loading || positionsHook.loading);
  // Track if we've ever loaded data - once loaded, never show loading spinner again
  const hasEverLoaded = useRef(false);

  // Surface hook errors so the UI doesn't hang on a failure. Both usePortfolio
  // and usePositions wrap their fetches in try/catch/finally with their own
  // 5s timeouts, so any real failure already turns hook.loading off and sets
  // hook.error. Mirror that into our local loading state so the page never
  // sits on a forever-spinner. Replaces the previous 15s "safety setTimeout"
  // which masked real bugs by force-setting loading=false on a wall-clock.
  useEffect(() => {
    if (portfolioHook.error || positionsHook.error) {
      if (loading) {
        console.error(
          '[PortfolioDashboard] Hook error — clearing loading',
          { portfolio: portfolioHook.error, positions: positionsHook.error }
        );
        setLoading(false);
        hasEverLoaded.current = true;
      }
    }
  }, [portfolioHook.error, positionsHook.error, loading]);
  // Modal manager keys are migrated as : Batch A (portfolio, taxExport,
  // snapshot), Batch B (csvImport, investmentSimulator), Batch C
  // (position, swap, addToken). The createAlert key + its companion
  // <CreateAlertModal> usage were dropped in V2.B1 — never .open()'d
  // from this file and `selectedAlertToken` was never set anywhere.
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);

  // BTC Comparison states - load from localStorage, sync with database
  const [showBTCComparison, setShowBTCComparisonState] = useState(() => {
    return StorageService.get<boolean>('show_btc_comparison', false);
  });
  const [btcHistoricalPrices, setBtcHistoricalPrices] = useState<Record<string, number>>({});
  const [btcCurrentPrice, setBtcCurrentPrice] = useState<number>(0);

  // Sync BTC comparison preference with database
  const setShowBTCComparison = useCallback((value: boolean) => {
    setShowBTCComparisonState(value);
    StorageService.set('show_btc_comparison', value);
    // Sync to database if authenticated
    userPreferencesService.setPreference('showBTCComparison', value);
  }, []);

  // Load BTC comparison preference from database on login
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      userPreferencesService.getPreference('showBTCComparison').then((value) => {
        if (value !== undefined && value !== showBTCComparison) {
          setShowBTCComparisonState(value);
          StorageService.set('show_btc_comparison', value);
        }
      });
    }
  }, [isAuthenticated, user?.id]);

  // Prices cache tracking — shape moved to `src/types/portfolio.ts`
  // so the toolbar/selector bars can share the same type without
  // importing from a component file.
  const [pricesCacheInfo, setPricesCacheInfo] = useState<PricesCacheInfo>({ fromCache: 0, fromAPI: 0 });

  // Filters state
  const [filters, setFilters] = useState<FilterConfig>(() => {
    const saved = StorageService.get<FilterConfig>('portfolio_filters', DEFAULT_FILTERS);
    return saved;
  });

  // V2.B4.1 — saved filter views (named filter combinations the user
  // can recall later). Backed by `portfolio_saved_filter_views` in
  // localStorage via the hook.
  const savedViews = usePortfolioSavedFilterViews();
  const hasActiveFilters =
    filters.search !== '' ||
    filters.profitLoss !== 'all' ||
    filters.minValue !== '' ||
    filters.maxValue !== '' ||
    filters.dateFrom !== '' ||
    filters.dateTo !== '';

  // Debounced search to avoid re-rendering on every keystroke (Performance Optimization)
  const debouncedSearch = useDebouncedValue(filters.search, 300);

  // UX preferences
  const [compactMode, setCompactMode] = useState(() => {
    return StorageService.get<boolean>('compact_mode', false);
  });
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  // V2.I.4: V3 "Glass Hero" header is now the DEFAULT (opt-out instead of
  // opt-in) — it's been validated and iterated on across V2.H{,.5,.6,.7}.
  // The two old flags survive as escape hatches:
  //   localStorage.setItem('disable_dashboard_header_v3', 'true') → V1
  //   localStorage.setItem('enable_dashboard_header_v2', 'true')  → V2 (legacy)
  // V3 takes precedence over V2 takes precedence over V1.
  const disableV3 = StorageService.get<boolean>('disable_dashboard_header_v3', false);
  const useDashboardHeaderV2 = StorageService.get<boolean>('enable_dashboard_header_v2', false);
  const useDashboardHeaderV3 = !disableV3;

  // View mode state (normal or 3D universe)
  const [viewMode, setViewMode] = useState<'normal' | '3d'>('normal');

  // V2.G7.5: showDrawdown / showHoldingDuration removed along with the
  // duplicated widgets (charts now canonicalized in analytics/*).

  // Dashboard customization state
  const [isEditMode, setIsEditMode] = useState(false);

  // V2.B4 — active layout preset. Initialized from localStorage so the
  // user's choice survives reloads. `gridKey` is bumped each time the
  // preset changes to force `<DashboardGrid>` to remount and re-read
  // the freshly-written layout from `DASHBOARD_STORAGE_KEY`.
  const [activePresetId, setActivePresetId] = useState<PortfolioLayoutPresetId>(() => {
    const saved = StorageService.get<PortfolioLayoutPresetId>(DASHBOARD_PRESET_STORAGE_KEY, 'standard');
    return (saved && saved in PORTFOLIO_LAYOUT_PRESETS) ? saved : 'standard';
  });
  const [gridKey, setGridKey] = useState(0);

  const handleSelectPreset = useCallback((presetId: string) => {
    if (!(presetId in PORTFOLIO_LAYOUT_PRESETS)) return;
    const id = presetId as PortfolioLayoutPresetId;
    // Build a Layout from the preset (id + cols + ordered index).
    const layout = PORTFOLIO_LAYOUT_PRESETS[id].map((entry, index) => ({
      id: entry.id,
      order: index,
      cols: entry.cols,
    }));
    StorageService.set(DASHBOARD_STORAGE_KEY, layout);
    StorageService.set(DASHBOARD_PRESET_STORAGE_KEY, id);
    setActivePresetId(id);
    // Force <DashboardGrid> to re-init its useState from localStorage.
    setGridKey((k) => k + 1);
  }, []);

  const toast = useToast();
  const { confirm, confirmationProps } = useConfirmation();
  const [isDark] = useDarkMode();
  const { isNew: isNewToken, markAsNew: markTokenAsNew } = useNewItemHighlight({ duration: 3000 });
  const tokenInputRef = useRef<HTMLInputElement>(null);
  const dashboardRef = useRef<HTMLDivElement>(null);

  // 🎉 Portfolio ATH tracking for celebrations
  const portfolioATHRef = useRef<number>(0);
  const celebrationCooldownRef = useRef<number>(0);
  const previousPLPctRef = useRef<number | null>(null);

  // React Query: Load coins list for autocomplete
  const { data: coins = FALLBACK_COINS } = useCoinsList();

  // React Query: Fetch real-time prices for portfolio tokens (auto-refresh every 60s)
  const tokenIds = useMemo(() => tokens.map(t => t.id), [tokens]);
  const {
    data: portfolioPrices = {},
    isLoading: pricesLoading,
    error: pricesError,
    isSuccess: pricesSuccess,
    dataUpdatedAt: pricesUpdatedAt
  } = useCoinPrices(tokenIds, {
    enabled: tokenIds.length > 0,
    refetchInterval: 60000, // Refresh prices every 60 seconds
  });

  // React Query: Fetch BTC price separately if not in portfolio (for BTC comparison)
  const btcNeeded = showBTCComparison && !tokenIds.includes('bitcoin');
  const { data: btcPriceData } = useCoinPrices(['bitcoin'], {
    enabled: btcNeeded,
    refetchInterval: 60000,
  });

  // Load cached prices on mount (fallback before API loads)
  const [cachedPrices, setCachedPrices] = useState<Prices>({});
  useEffect(() => {
    const cachedData = StorageService.get<{ prices: Prices; timestamp: number; exportDate?: string }>('cached_prices');
    if (cachedData && cachedData.prices) {
      setCachedPrices(cachedData.prices);

      // Calculate cache age
      const ageMs = Date.now() - cachedData.timestamp;
      const ageMinutes = Math.floor(ageMs / 60000);
      const ageHours = Math.floor(ageMs / 3600000);
      const ageDays = Math.floor(ageMs / 86400000);

      let ageStr = '';
      if (ageDays > 0) ageStr = `${ageDays}j`;
      else if (ageHours > 0) ageStr = `${ageHours}h`;
      else if (ageMinutes > 0) ageStr = `${ageMinutes}m`;
      else ageStr = 'maintenant';

      setPricesCacheInfo({
        fromCache: Object.keys(cachedData.prices).length,
        fromAPI: 0,
        cacheTimestamp: cachedData.timestamp,
        cacheAge: ageStr
      });

      logger.info('PortfolioDashboard', `💾 Loaded ${Object.keys(cachedData.prices).length} cached price(s) from ${cachedData.exportDate || 'unknown date'} (${ageStr} ago)`);
    }
  }, []);

  // Merge BTC price with portfolio prices, and fallback to cached prices if needed
  const prices = useMemo(() => {
    let mergedPrices = { ...portfolioPrices };

    // If API hasn't loaded yet, use cached prices as fallback
    if (pricesLoading && Object.keys(cachedPrices).length > 0) {
      mergedPrices = { ...cachedPrices, ...portfolioPrices };
    }

    if (btcNeeded && btcPriceData) {
      mergedPrices = { ...mergedPrices, ...btcPriceData };
    }

    return mergedPrices;
  }, [portfolioPrices, btcPriceData, btcNeeded, cachedPrices, pricesLoading]);

  // Update cache info when API prices arrive
  useEffect(() => {
    if (pricesSuccess && Object.keys(portfolioPrices).length > 0) {
      setPricesCacheInfo(prev => ({
        ...prev,
        fromAPI: Object.keys(portfolioPrices).length,
        cacheAge: undefined // Clear cache age when we have fresh data
      }));
    }
  }, [pricesSuccess, portfolioPrices]);

  // Show toast notifications only for first load and errors
  const firstPriceLoad = useRef(true);
  useEffect(() => {
    if (pricesSuccess && Object.keys(prices).length > 0 && tokenIds.length > 0 && firstPriceLoad.current) {
      const messages = [];
      if (pricesCacheInfo.fromCache > 0 && pricesCacheInfo.cacheAge) {
        messages.push(`💾 ${pricesCacheInfo.fromCache} du cache (${pricesCacheInfo.cacheAge})`);
      }
      messages.push(`🔄 ${tokenIds.length} actualisés`);

      toast(`✅ Prix: ${messages.join(', ')}`, 'success', 3000);
      logger.info('PortfolioDashboard', `✅ Fetched prices for ${tokenIds.length} tokens`);
      firstPriceLoad.current = false;
    }
  }, [pricesSuccess, prices, tokenIds.length, pricesCacheInfo]);

  useEffect(() => {
    if (pricesError) {
      logger.error('PortfolioDashboard', 'Error fetching prices', pricesError);
      toast(`⚠️ ${t('portfolioDashboard.pricesUnavailable')}`, 'warning', 4000);
    }
  }, [pricesError]);

  // Force disable keyboard shortcuts help on touch devices
  useEffect(() => {
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice && showShortcutsHelp) {
      setShowShortcutsHelp(false);
    }
  }, [showShortcutsHelp]);

  // 🔄 HYBRID MODE: Sync hook data with local state
  useEffect(() => {
    console.log('[HYBRID] Mode:', portfolioHook.mode, 'portfolioLoading:', portfolioHook.loading, 'positionsLoading:', positionsHook.loading, 'hasEverLoaded:', hasEverLoaded.current);

    // Update loading state - show loading only during INITIAL load
    // Once we've loaded data once, NEVER show loading spinner again (background refetch is invisible)
    const hooksStillLoading = portfolioHook.loading || positionsHook.loading;

    // CRITICAL FIX: Once hasEverLoaded is true, NEVER show loading spinner again
    if (hasEverLoaded.current) {
      if (loading) {
        console.log('[HYBRID] hasEverLoaded=true, forcing loading=false');
        setLoading(false);
      }
    } else if (!hooksStillLoading) {
      // Initial load complete
      console.log('[HYBRID] Hooks done loading (initial), setting loading=false');
      setLoading(false);
      hasEverLoaded.current = true;
    } else if (tokens.length > 0) {
      // We have tokens from cache, hide loading
      console.log('[HYBRID] Have cached tokens, setting loading=false');
      setLoading(false);
      hasEverLoaded.current = true;
    }
    // else: still in initial loading, keep loading=true

    // Sync active portfolio
    if (portfolioHook.activePortfolio) {
      const hybridPortfolio = portfolioHook.activePortfolio;

      // V2.G12.2: narrow on the union — cloud Portfolio uses snake_case
      // (`created_at`), LocalPortfolio uses camelCase (`createdAt`).
      const created = 'created_at' in hybridPortfolio
        ? hybridPortfolio.created_at
        : hybridPortfolio.createdAt;
      const updated = 'updated_at' in hybridPortfolio
        ? hybridPortfolio.updated_at
        : hybridPortfolio.updatedAt;

      // Convert to legacy format (without tokens array, tokens handled separately)
      const legacyPortfolio: LegacyPortfolio = {
        id: hybridPortfolio.id,
        name: hybridPortfolio.name || 'Portfolio',
        description: hybridPortfolio.description || undefined,
        tokens: [], // Empty, tokens managed separately
        createdAt: new Date(created).getTime(),
        updatedAt: new Date(updated).getTime(),
        color: hybridPortfolio.color || undefined,
        icon: hybridPortfolio.icon || undefined,
      };

      setActivePortfolio(legacyPortfolio);
    }

    // 🔄 HYBRID MODE: Combine tokens + positions into legacy Token format
    if (portfolioHook.tokens.length > 0 || positionsHook.positions.length > 0) {
      console.log('[HYBRID] Combining tokens and positions...');
      console.log('[HYBRID] Tokens:', portfolioHook.tokens.length);
      console.log('[HYBRID] Positions:', positionsHook.positions.length);

      // Group positions by token
      const positionsByToken = new Map<string, Position[]>();

      positionsHook.positions.forEach((hybridPos) => {
        // Convert hybrid position to legacy Position format
        // IMPORTANT: In cloud mode, use coin_id (not token_id UUID) to match tokens
        // token_id is UUID in cloud, coin_id is the CoinGecko ID ('bitcoin', 'ethereum', etc.)
        const tokenId = (hybridPos as any).coin_id || ('token_id' in hybridPos ? hybridPos.token_id : hybridPos.tokenId);

        const legacyPosition: Position = {
          id: hybridPos.id,
          // V2.G12.4: cloud Position.type is `string` in the generated
          // Supabase types ; narrow to the legacy enum.
          type: hybridPos.type as Position['type'],
          date: hybridPos.date,
          quantity: typeof hybridPos.quantity === 'string' ? parseFloat(hybridPos.quantity) : hybridPos.quantity,
          price: typeof hybridPos.price === 'string' ? parseFloat(hybridPos.price) : hybridPos.price,
          note: hybridPos.note || undefined,
          // ✨ Cost Basis Chain Tracking - Preserve swap metadata during conversion
          is_swap: (hybridPos as any).is_swap,
          swap_chain_id: (hybridPos as any).swap_chain_id,
          original_cost_basis: (hybridPos as any).original_cost_basis,
        };

        const existing = positionsByToken.get(tokenId) || [];
        positionsByToken.set(tokenId, [...existing, legacyPosition]);
      });

      // Convert hybrid tokens to legacy Token format
      // V2.G12.3: same as-cast narrowing pattern as useLegacyTokensFromHybrid
      // (V2.G4.1) — runtime equivalent, cleaner types.
      let legacyTokens: Token[] = portfolioHook.tokens.map((hybridToken) => {
        const tokenId =
          'coin_id' in hybridToken
            ? (hybridToken as { coin_id: string }).coin_id
            : hybridToken.id;
        const symbol = (hybridToken as { symbol?: string }).symbol;
        const name = (hybridToken as { name?: string }).name;
        const displayName = (symbol || name || tokenId || 'UNKNOWN').toUpperCase();

        const targetPrice =
          (hybridToken as { targetPrice?: number }).targetPrice ??
          (() => {
            const tp = (hybridToken as { target_price?: string | number | null }).target_price;
            return tp != null ? Number(tp) : undefined;
          })();

        return {
          id: tokenId,
          displayName,
          positions: positionsByToken.get(tokenId) || [],
          note: (hybridToken as { notes?: string | null }).notes ?? undefined,
          tags: [],
          targetPrice,
        };
      });

      // 🔧 FIX: If legacyTokens is empty but we have positions, read directly from PortfolioManager
      if (legacyTokens.length === 0 && positionsHook.positions.length > 0 && portfolioHook.activePortfolio) {
        console.warn('[HYBRID] Tokens empty but positions exist - reading from PortfolioManager as fallback');
        const pmPortfolio = PortfolioManager.getPortfolio(portfolioHook.activePortfolio.id);
        if (pmPortfolio && pmPortfolio.tokens && pmPortfolio.tokens.length > 0) {
          legacyTokens = pmPortfolio.tokens.map(token => ({
            id: token.id,
            displayName: (token.displayName || token.id || 'UNKNOWN').toUpperCase(),
            positions: positionsByToken.get(token.id) || token.positions || [],
            note: token.note,
            tags: token.tags || [],
            targetPrice: token.targetPrice,
          }));
          console.log('[HYBRID] Recovered tokens from PortfolioManager:', legacyTokens.length);
        }
      }

      // Set tokens only when we have something to set. The previous condition
      // (`legacyTokens.length > 0 || tokens.length === 0`) was equivalent in
      // observable behavior — when both legacy and current are empty, the
      // setTokens call was a no-op. Simplifying to the explicit predicate
      // avoids confusion when tracing the hybrid sync flow.
      if (legacyTokens.length > 0) {
        console.log('[HYBRID] Combined tokens:', legacyTokens.length);
        setTokens(legacyTokens);
      } else if (tokens.length > 0) {
        // We had tokens, hook returned an empty mapping mid-refetch — keep
        // the existing UI rather than flashing an empty state.
        console.warn('[HYBRID] Skipping setTokens - would clear existing tokens during refetch');
      }
    } else if (portfolioHook.tokens.length === 0 && positionsHook.positions.length === 0) {
      // Hooks are settled and report no data — explicit empty state.
      if (!portfolioHook.loading && !positionsHook.loading) {
        setTokens([]);
      }
    }
  }, [
    portfolioHook.mode,
    portfolioHook.loading,
    portfolioHook.activePortfolio,
    portfolioHook.tokens,
    positionsHook.loading,
    positionsHook.positions,
  ]);

  // 🔄 Sync tokens to PortfolioManager for TopBar export functionality
  useEffect(() => {
    if (tokens.length > 0 && portfolioHook.activePortfolio) {
      const portfolioId = portfolioHook.activePortfolio.id;
      PortfolioManager.updatePortfolioTokens(portfolioId, tokens);
    }
  }, [tokens, portfolioHook.activePortfolio]);

  // 🔄 Sync prices to cache for TopBar export functionality
  useEffect(() => {
    if (Object.keys(prices).length > 0) {
      StorageService.set('cached_prices', { prices, timestamp: Date.now() });
    }
  }, [prices]);

  // Check price alerts when prices update
  useEffect(() => {
    if (Object.keys(prices).length > 0) {
      alertService.checkAlerts(prices);
    }
  }, [prices]);

  // 🔄 HYBRID MODE: Token saving handled by usePortfolio() hook automatically
  // No need for manual saving anymore!

  // Load BTC historical prices and current price when BTC comparison is enabled
  useEffect(() => {
    if (!showBTCComparison || tokens.length === 0) return;

    const loadBTCPrices = async () => {
      try {
        // Get all unique buy dates from all positions
        const buyDates = new Set<string>();
        tokens.forEach(token => {
          token.positions.forEach(position => {
            if (position.type === 'buy') {
              buyDates.add(position.date);
            }
          });
        });

        if (buyDates.size === 0) {
          toast(`ℹ️ ${t('portfolioDashboard.noBuyDatesForBtc')}`, 'info', 3000);
          return;
        }

        // Fetch historical BTC prices for these dates
        toast(`⏳ ${t('portfolioDashboard.loadingBtcPrices', { count: buyDates.size })}`, 'info', 2000);
        logger.info('PortfolioDashboard', `📊 Loading BTC prices for ${buyDates.size} dates...`);
        const historicalPrices = await BTCPriceHistoryService.getBTCPricesForDates(Array.from(buyDates));
        setBtcHistoricalPrices(historicalPrices);

        // Get current BTC price from React Query (always available)
        const btcPrice = prices['bitcoin']?.usd || 0;
        setBtcCurrentPrice(btcPrice);

        const loadedCount = Object.keys(historicalPrices).length;
        toast(`✅ ${t('portfolioDashboard.btcPricesLoaded', { count: loadedCount })}`, 'success', 3000);
      } catch (error) {
        logger.error('PortfolioDashboard', 'Error loading BTC prices', error);
        toast(`❌ ${t('portfolioDashboard.btcPricesError')}`, 'error', 5000);
      }
    };

    loadBTCPrices();
  }, [showBTCComparison, tokens, prices]);

  // Calculate results for all tokens using selected calculation method
  // Memoized to avoid recalculation on every render (Performance Optimization)
  const fifoResults: FIFOResults = useMemo(() => {
    const results: FIFOResults = {};
    tokens.forEach(token => {
      results[token.id] = CalculatorFactory.calculate(token.positions, calculationMethod);
    });
    return results;
  }, [tokens, calculationMethod]);

  // Calculate total portfolio metrics (Memoized for performance)
  const portfolioMetrics = useMemo(() => {
    // Calculate EUR/USD exchange rate from any available price with both currencies
    let eurUsdRate = 1;
    if (currency === 'EUR') {
      // Find a token with both USD and EUR prices to calculate the rate
      for (const token of tokens) {
        const price = prices[token.id];
        if (price?.usd && price?.eur) {
          eurUsdRate = price.eur / price.usd;
          break;
        }
      }
      // Fallback: try bitcoin price
      if (eurUsdRate === 1 && prices['bitcoin']?.usd && prices['bitcoin']?.eur) {
        eurUsdRate = prices['bitcoin'].eur / prices['bitcoin'].usd;
      }
    }

    const totalValue = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      const price = prices[token.id];
      // Use EUR price if available and currency is EUR, otherwise use USD and convert
      const currentPrice = currency === 'EUR'
        ? (price?.eur || (price?.usd || 0) * eurUsdRate)
        : (price?.usd || 0);
      const qty = fifo.remainingQty || 0;
      return sum + (qty * currentPrice);
    }, 0);

    // totalCost is stored in USD, convert if currency is EUR
    const totalCostUsd = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      return sum + (fifo.totalCost || 0);
    }, 0);
    const totalCost = currency === 'EUR' ? totalCostUsd * eurUsdRate : totalCostUsd;

    // realizedPL is stored in USD, convert if currency is EUR
    const totalRealizedPLUsd = tokens.reduce((sum, token) => {
      const fifo = fifoResults[token.id] || {};
      return sum + (fifo.realizedPL || 0);
    }, 0);
    const totalRealizedPL = currency === 'EUR' ? totalRealizedPLUsd * eurUsdRate : totalRealizedPLUsd;

    const totalUnrealizedPL = totalValue - totalCost;
    const totalPL = totalRealizedPL + totalUnrealizedPL;
    const totalPLPct = totalCost > 0 ? (totalPL / totalCost) * 100 : 0;

    return {
      totalValue,
      totalCost,
      totalRealizedPL,
      totalUnrealizedPL,
      totalPL,
      totalPLPct
    };
  }, [tokens, fifoResults, prices, currency]);

  // Destructure for backward compatibility
  const { totalValue, totalCost, totalRealizedPL, totalUnrealizedPL, totalPL, totalPLPct } = portfolioMetrics;

  // 🎉 Load stored Portfolio ATH on mount
  useEffect(() => {
    const storedATH = StorageService.get<number>('portfolio_ath', 0);
    portfolioATHRef.current = storedATH;
  }, []);

  // 🎉 Portfolio ATH detection - trigger confetti on new ATH
  useEffect(() => {
    if (totalValue < 100 || loading) return;

    const now = Date.now();
    if (now - celebrationCooldownRef.current < 10000) return; // 10s cooldown

    // Check if new ATH (with 0.1% threshold to avoid noise)
    if (totalValue > portfolioATHRef.current * 1.001) {
      const isFirstATH = portfolioATHRef.current === 0;
      portfolioATHRef.current = totalValue;
      StorageService.set('portfolio_ath', totalValue);

      if (!isFirstATH) {
        celebrations.triggerATH('Portfolio', `${currencyInfo.symbol}${totalValue.toLocaleString()}`);
        celebrationCooldownRef.current = now;
      }
    }
  }, [totalValue, loading, celebrations, currencyInfo.symbol]);

  // 🎉 Fireworks for significant gains crossing thresholds
  useEffect(() => {
    if (loading || totalPLPct === 0) return;

    const prev = previousPLPctRef.current;
    previousPLPctRef.current = totalPLPct;

    if (prev === null) return; // First render, skip

    const now = Date.now();
    if (now - celebrationCooldownRef.current < 10000) return; // 10s cooldown

    // Check if we crossed a positive threshold
    const thresholds = [100, 50, 25, 10];
    for (const threshold of thresholds) {
      if (prev < threshold && totalPLPct >= threshold) {
        celebrations.triggerMilestone(
          threshold >= 100 ? 'gain_100' : threshold >= 50 ? 'gain_50' : threshold >= 25 ? 'gain_25' : 'gain_10',
          { percentGain: totalPLPct }
        );
        celebrationCooldownRef.current = now;
        break;
      }
    }
  }, [totalPLPct, loading, celebrations]);

  // Calculate weighted average "% vs BTC" for entire portfolio
  const portfolioVsBTC = useMemo(() => {
    if (!showBTCComparison || btcCurrentPrice === 0 || tokens.length === 0) return null;

    let totalBuyValue = 0;
    let weightedSum = 0;

    tokens.forEach(token => {
      const currentPrice = prices[token.id]?.usd || 0;

      token.positions.forEach(position => {
        if (position.type === 'buy') {
          const buyValue = position.quantity * position.price;
          const btcPriceAtBuy = btcHistoricalPrices[position.date] || 0;

          if (btcPriceAtBuy > 0 && currentPrice > 0) {
            // Token performance since buy
            const tokenPerf = ((currentPrice - position.price) / position.price) * 100;

            // BTC performance since buy
            const btcPerf = ((btcCurrentPrice - btcPriceAtBuy) / btcPriceAtBuy) * 100;

            // Difference (positive = outperformed BTC)
            const vsBTC = tokenPerf - btcPerf;

            totalBuyValue += buyValue;
            weightedSum += buyValue * vsBTC;
          }
        }
      });
    });

    return totalBuyValue > 0 ? weightedSum / totalBuyValue : null;
  }, [showBTCComparison, btcCurrentPrice, btcHistoricalPrices, tokens, prices]);

  // Prepare enriched positions for 3D visualization
  const enrichedPositions = useMemo(() => {
    const allPositions: any[] = [];

    tokens.forEach(token => {
      const fifo = fifoResults[token.id] || {};
      const currentPrice = prices[token.id]?.usd || 0;
      const priceChange = prices[token.id]?.usd_24h_change || 0;

      // Get coin data from API for more info
      const coin = coins.find(c => c.id === token.id);

      // V2.G12.5: previously read `fifo.unrealizedPL` (doesn't exist on
      // CalculatorResult — was always 0) and referenced `realizedPL`
      // which wasn't declared in scope (would crash at runtime in 3D mode).
      // Now compute both from the actual fields.
      const remainingQty = fifo.remainingQty || 0;
      const currentValue = remainingQty * currentPrice;
      const totalCost = fifo.totalCost || 0;
      const realizedPL = fifo.realizedPL || 0;
      const unrealizedPL = currentValue - totalCost;

      // Estimate volatility from price change (rough approximation)
      const volatility = Math.abs(priceChange) * 2; // Simple approximation

      // Add one enriched position per token (aggregated)
      if (remainingQty > 0) {
        allPositions.push({
          id: token.id,
          type: 'buy' as const,
          date: token.positions[0]?.date || '',
          quantity: remainingQty,
          price: currentPrice,
          // Additional properties for 3D viz
          coinId: token.id,
          coinSymbol: coin?.symbol || token.displayName?.split(' ')[0] || token.id?.toUpperCase() || 'UNKNOWN',
          coinName: coin?.name || token.displayName || 'Unknown',
          currentValue: currentValue,
          currentPrice: currentPrice,
          unrealizedPL: unrealizedPL,
          realizedPL: realizedPL,
          volatility: volatility,
          volume24h: currentValue, // Use current value as proxy for volume
          priceChange: priceChange,
        });
      }
    });

    return allPositions;
  }, [tokens, fifoResults, prices, coins]);

  // Filter and sort tokens
  const filteredTokens = useMemo(() => {
    let result = [...tokens];

    // Filter by search text (using debounced value for performance)
    if (debouncedSearch) {
      const searchLower = debouncedSearch.toLowerCase();
      result = result.filter(token =>
        token.displayName.toLowerCase().includes(searchLower) ||
        token.id.toLowerCase().includes(searchLower)
      );
    }

    // Filter by profit/loss
    if (filters.profitLoss !== 'all') {
      result = result.filter(token => {
        const fifo = fifoResults[token.id] || {};
        const currentPrice = prices[token.id]?.usd || 0;
        const qty = fifo.remainingQty || 0;
        const currentValue = qty * currentPrice;
        const totalCost = qty * (fifo.avgCost || 0);
        const unrealizedPL = currentValue - totalCost;

        return filters.profitLoss === 'profit' ? unrealizedPL > 0 : unrealizedPL < 0;
      });
    }

    // Filter by value range
    if (filters.minValue || filters.maxValue) {
      result = result.filter(token => {
        const fifo = fifoResults[token.id] || {};
        const currentPrice = prices[token.id]?.usd || 0;
        const qty = fifo.remainingQty || 0;
        const currentValue = qty * currentPrice;

        const min = filters.minValue ? parseFloat(filters.minValue) : -Infinity;
        const max = filters.maxValue ? parseFloat(filters.maxValue) : Infinity;

        return currentValue >= min && currentValue <= max;
      });
    }

    // Filter by date range
    if (filters.dateFrom || filters.dateTo) {
      result = result.filter(token => {
        // Get all buy dates for this token
        const buyDates = token.positions
          .filter(p => p.type === 'buy')
          .map(p => p.date);

        if (buyDates.length === 0) return false;

        const from = filters.dateFrom ? new Date(filters.dateFrom).getTime() : -Infinity;
        const to = filters.dateTo ? new Date(filters.dateTo).getTime() : Infinity;

        // Check if any buy date is within range
        return buyDates.some(date => {
          const dateTime = new Date(date).getTime();
          return dateTime >= from && dateTime <= to;
        });
      });
    }

    // Sort
    result.sort((a, b) => {
      const fifoA = fifoResults[a.id] || {};
      const fifoB = fifoResults[b.id] || {};
      const priceA = prices[a.id]?.usd || 0;
      const priceB = prices[b.id]?.usd || 0;

      let comparison = 0;

      switch (filters.sortBy) {
        case 'name':
          comparison = (a.displayName || a.id).localeCompare(b.displayName || b.id);
          break;
        case 'value': {
          const valueA = (fifoA.remainingQty || 0) * priceA;
          const valueB = (fifoB.remainingQty || 0) * priceB;
          comparison = valueA - valueB;
          break;
        }
        case 'pl': {
          const qtyA = fifoA.remainingQty || 0;
          const qtyB = fifoB.remainingQty || 0;
          const valueA = qtyA * priceA;
          const valueB = qtyB * priceB;
          const costA = qtyA * (fifoA.avgCost || 0);
          const costB = qtyB * (fifoB.avgCost || 0);
          const plA = valueA - costA;
          const plB = valueB - costB;
          comparison = plA - plB;
          break;
        }
        case 'plPct': {
          const qtyA = fifoA.remainingQty || 0;
          const qtyB = fifoB.remainingQty || 0;
          const valueA = qtyA * priceA;
          const valueB = qtyB * priceB;
          const costA = qtyA * (fifoA.avgCost || 0);
          const costB = qtyB * (fifoB.avgCost || 0);
          const plPctA = costA > 0 ? ((valueA - costA) / costA) * 100 : 0;
          const plPctB = costB > 0 ? ((valueB - costB) / costB) * 100 : 0;
          comparison = plPctA - plPctB;
          break;
        }
        case 'qty':
          comparison = (fifoA.remainingQty || 0) - (fifoB.remainingQty || 0);
          break;
      }

      return filters.sortOrder === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [tokens, debouncedSearch, filters.profitLoss, filters.minValue, filters.maxValue, filters.dateFrom, filters.dateTo, filters.sortBy, filters.sortOrder, fifoResults, prices]);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    StorageService.set('portfolio_filters', filters);
  }, [filters]);

  // Save compact mode preference
  useEffect(() => {
    StorageService.set('compact_mode', compactMode);
  }, [compactMode]);

  // Portfolio management functions
  const handlePortfolioChange = useCallback((portfolio: LegacyPortfolio) => {
    // 🔄 HYBRID MODE: Use hook to set active portfolio
    portfolioHook.setActivePortfolio(portfolio.id);
    toast(`${t('portfolioDashboard.portfolioActivated')}: "${portfolio.name}"`, 'success', 2000);

    // State will be updated automatically by the sync useEffect
  }, [portfolioHook, toast, t]);

  const handleCreatePortfolio = useCallback(() => {
    modals.open('portfolio');
  }, []);

  const handleSavePortfolio = useCallback(async (data: { name: string; description: string; icon: string; color: string }) => {
    try {
      // 🔄 HYBRID MODE: Use hook to create portfolio
      const newPortfolio = await portfolioHook.createPortfolio({
        name: data.name,
        description: data.description,
        icon: data.icon,
        color: data.color,
        is_default: false,
      });

      // Set as active
      await portfolioHook.setActivePortfolio(newPortfolio.id);

      toast(`${t('portfolioDashboard.portfolioCreated')}: "${newPortfolio.name}"`, 'success', 3000);

      // State will be updated automatically by the sync useEffect
    } catch (error) {
      console.error('[HYBRID] Error creating portfolio:', error);
      toast(t('portfolioDashboard.errorCreatingPortfolio'), 'error', 3000);
    }
  }, [portfolioHook, toast, t]);

  // Handle adding a new token
  const handleAddToken = useCallback(async (coinId: string, symbol: string) => {
    try {
      // Check if token already exists
      if (tokens.find(t => t.id === coinId)) {
        toast(`${symbol} ${t('portfolioDashboard.tokenAlreadyExists')}`, 'warning');
        return;
      }

      // 🔄 HYBRID MODE: Use hook to add token
      await portfolioHook.addToken({
        id: coinId,
        symbol: symbol.toUpperCase(),
        display_name: symbol,
      });

      toast(`${symbol} ${t('portfolioDashboard.tokenAdded')}`, 'success');

      // Track token addition in analytics (with token symbol)

      // Mark token as new for shimmer-highlight effect
      markTokenAsNew(coinId);

      // Close add token modal if open
      modals.close('addToken');

      // Open position modal immediately (state will be updated by sync useEffect)
      const newToken: Token = {
        id: coinId,
        displayName: symbol.toUpperCase(),
        positions: []
      };
      setSelectedToken(newToken);
      setSelectedPosition(null);
      modals.open('position');
    } catch (error) {
      console.error('[HYBRID] Error adding token:', error);
      toast(t('portfolioDashboard.errorAddingToken'), 'error');
    }
  }, [tokens, portfolioHook, toast, t, markTokenAsNew]);

  // Handle opening add token modal for empty portfolio
  const handleOpenAddTokenModal = useCallback(() => {
    modals.open('addToken');
  }, []);

  // Handle adding a position to existing token
  const handleAddPosition = useCallback((token?: Token) => {
    if (token) {
      setSelectedToken(token);
      setSelectedPosition(null);
      modals.open('position');
    }
  }, []);

  // Handle saving a position (add or edit)
  const handleSavePosition = useCallback(async (position: Partial<Position>) => {
    if (!selectedToken) return;

    try {
      // 🔄 HYBRID MODE: Use hook to add/update position
      if (selectedPosition) {
        // Editing existing position
        await positionsHook.updatePosition(selectedPosition.id, {
          type: position.type,
          date: position.date,
          quantity: position.quantity,
          price: position.price,
          note: position.note || null,
        });
        toast(t('portfolioDashboard.positionEdited'), 'success');
      } else {
        // Adding new position
        await positionsHook.addPosition({
          tokenId: selectedToken.id,
          type: position.type || 'buy',
          date: position.date || new Date().toISOString().split('T')[0],
          quantity: position.quantity || 0,
          price: position.price || 0,
          note: position.note,
        });
        toast(t('portfolioDashboard.positionAdded'), 'success');
      }

      // State will be updated automatically by sync useEffect
      modals.close('position');
      setSelectedToken(null);
      setSelectedPosition(null);
    } catch (error) {
      console.error('[HYBRID] Error saving position:', error);
      toast(t('portfolioDashboard.errorSaving'), 'error');
    }
  }, [selectedToken, selectedPosition, positionsHook, toast, t]);

  // Handle editing a position
  const handleEditPosition = useCallback((token: Token, position: Position) => {
    setSelectedToken(token);
    setSelectedPosition(position);
    modals.open('position');
  }, []);

  // Handle deleting a position
  const handleDeletePosition = useCallback(async (tokenId: string, positionId: string) => {
    try {
      // 🔄 HYBRID MODE: Use hook to delete position
      await positionsHook.deletePosition(positionId);

      // Check if token has remaining positions after delete
      // If not, optionally delete the token too
      const token = tokens.find(tk => tk.id === tokenId);
      if (token && token.positions.length <= 1) {
        // This was the last position, consider deleting the token
        const shouldDeleteToken = await confirm({
          title: t('portfolioDashboard.deleteToken'),
          message: t('portfolioDashboard.deleteLastPosition'),
          confirmText: t('common.delete'),
          cancelText: t('common.cancel'),
          variant: 'warning'
        });
        if (shouldDeleteToken) {
          await portfolioHook.deleteToken(tokenId);
        }
      }

      toast(t('portfolioDashboard.positionDeleted'), 'info');
      // State will be updated automatically by sync useEffect
    } catch (error) {
      console.error('[HYBRID] Error deleting position:', error);
      toast(t('portfolioDashboard.errorDeleting'), 'error');
    }
  }, [tokens, positionsHook, portfolioHook, toast, t, confirm]);

  // Handle deleting a token
  const handleDeleteToken = useCallback(async (tokenId: string) => {
    const confirmed = await confirm({
      title: t('portfolioDashboard.deleteToken'),
      message: t('portfolioDashboard.confirmDeleteToken'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
      variant: 'danger'
    });
    if (confirmed) {
      try {
        // 🔄 HYBRID MODE: Use hook to delete token
        await portfolioHook.deleteToken(tokenId);
        toast(t('portfolioDashboard.tokenDeleted'), 'info');
        // State will be updated automatically by sync useEffect
      } catch (error) {
        console.error('[HYBRID] Error deleting token:', error);
        toast(t('portfolioDashboard.errorDeletingToken'), 'error');
      }
    }
  }, [portfolioHook, toast, t, confirm]);


  // Handle swap
  const handleSwap = useCallback(async (fromId: string, toId: string, amount: number, feePct: number, date: string) => {
    try {
      // Calculate the value in USD
      const fromPrice = prices[fromId]?.usd || 0;
      const toPrice = prices[toId]?.usd || 0;

      if (!fromPrice || !toPrice) {
        toast(t('portfolioDashboard.pricesUnavailableForSwap'), 'error');
        return;
      }

      // ✨ Cost Basis Tracking: Calculate original cost basis
      const fromToken = tokens.find(t => t.id === fromId);
      const originalCostBasisTotal = calculateOriginalCostBasis(fromToken, amount);

      // Generate or reuse swap chain ID
      const existingChainId = fromToken?.positions?.[0]?.swap_chain_id;
      const swapChainId = existingChainId || generateSwapChainId();

      // Calculate proceeds after fees
      const proceedsUsd = amount * fromPrice * (1 - feePct);
      const toAmount = proceedsUsd / toPrice;

      // ✨ Calculate cost basis per unit for the new token
      const costBasisPerUnit = calculateCostBasisPerUnit(originalCostBasisTotal, toAmount);

      // 🔄 HYBRID MODE: Use hooks to create swap positions

      // 1. Add sell position to FROM token with cost basis tracking
      await positionsHook.addPosition({
        tokenId: fromId,
        type: 'sell',
        date,
        quantity: amount,
        price: fromPrice,
        note: `Swap vers ${toId} (frais: ${feePct * 100}%)`,
        // ✨ Cost Basis Chain Tracking
        original_cost_basis: originalCostBasisTotal / amount,  // Per unit
        is_swap: true,
        swap_chain_id: swapChainId,
      });

      // 2. Check if TO token exists, create if needed
      // 🔍 IMPORTANT: Check in PortfolioManager (source of truth), not in portfolioHook.tokens (async)
      const pmPortfolio = PortfolioManager.getPortfolio(portfolioHook.activePortfolio?.id || 'default');
      const toTokenExists = pmPortfolio?.tokens.find(t => t.id === toId);

      console.log('🔍 [handleSwap] Checking if TO token exists in PortfolioManager:', {
        toId,
        exists: !!toTokenExists,
        portfolioId: portfolioHook.activePortfolio?.id,
        totalTokens: pmPortfolio?.tokens.length
      });

      if (!toTokenExists) {
        console.log('🆕 [handleSwap] Creating new TO token:', toId);
        // Create TO token first
        const toCoin = coins.find(c => c.id === toId);
        await portfolioHook.addToken({
          id: toId,
          symbol: toCoin?.symbol.toUpperCase() || toId,
          display_name: toCoin?.name || toId,
        });
      } else {
        console.log('✅ [handleSwap] TO token already exists, reusing it');
      }

      // 3. Add buy position to TO token with cost basis tracking
      await positionsHook.addPosition({
        tokenId: toId,
        type: 'buy',
        date,
        quantity: toAmount,
        price: toPrice,
        note: `Swap depuis ${fromId}`,
        // ✨ Cost Basis Chain Tracking - Preserve original acquisition cost
        original_cost_basis: costBasisPerUnit,
        is_swap: true,
        swap_chain_id: swapChainId,
      });

      toast(`${t('portfolioDashboard.swapSuccess')}: ${fmt(amount, 8)} ${fromId.toUpperCase()} → ${fmt(toAmount, 8)} ${toId.toUpperCase()}`, 'success');
      modals.close('swap');
      setSelectedToken(null);
      // State will be updated automatically by sync useEffect
    } catch (error) {
      console.error('[HYBRID] Error during swap:', error);
      toast(t('portfolioDashboard.swapError'), 'error');
    }
  }, [prices, tokens, coins, positionsHook, portfolioHook, toast, t]);

  // Handle export CSV
  const handleExportCSV = useCallback(() => {
    exportPortfolioCSV(tokens, prices, fifoResults);
    toast(t('portfolioDashboard.exportedCSV'), 'success');
  }, [tokens, prices, fifoResults, toast, t]);

  // Handle export JSON using shared backup service
  const handleExportJSON = useCallback(() => {
    // Pass current state data for more accurate export
    // Note: Pass undefined for athCache to let backupService use ATHService.loadCache()
    const result = exportBackup(tokens, prices, btcHistoricalPrices, undefined);
    if (result.success) {
      toast(`✅ ${t('portfolioDashboard.exportedJSON')}: ${result.summary}`, 'success');
    } else {
      toast(t('portfolioDashboard.importError'), 'error');
    }
  }, [tokens, prices, btcHistoricalPrices, toast, t]);

  // Handle import JSON using shared backup service
  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';

    input.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        toast(t('portfolioDashboard.noFileSelected'), 'warning');
        return;
      }

      importBackup(
        file,
        (result) => {
          // Update local state for immediate UI update (no page reload needed)
          setTokens(result.tokens);

          // Update BTC historical prices state if available
          if (result.btcHistoricalPrices) {
            setBtcHistoricalPrices(result.btcHistoricalPrices);
          }

          const summary = getImportSummary(result.counts);
          toast(`✅ Portfolio importé: ${summary}`, 'success');
          logger.info('PortfolioDashboard', `✅ Portfolio imported successfully`);
        },
        (errorMessage) => {
          logger.error('PortfolioDashboard', 'Import error', errorMessage);
          toast(errorMessage, 'error');
        }
      );
    };

    input.click();
  }, [toast, t]);

  // Handle CSV import from exchanges (Binance, Coinbase, Kraken)
  const handleCSVImport = useCallback(async (importData: {
    coinId: string;
    symbol: string;
    positions: {
      type: 'buy' | 'sell' | 'mining' | 'staking';
      date: string;
      quantity: number;
      price: number;
      note?: string;
    }[];
  }[]) => {
    const portfolioId = portfolioHook.activePortfolio?.id || 'default';
    let tokensAdded = 0;
    let positionsAdded = 0;

    for (const tokenData of importData) {
      // Check if token already exists
      let existingToken = tokens.find(t => t.id === tokenData.coinId);

      if (!existingToken) {
        // Add new token
        const newToken: Token = {
          id: tokenData.coinId,
          displayName: tokenData.symbol.toUpperCase(),
          positions: [],
        };

        // Add token via hook
        await portfolioHook.addToken({
          id: tokenData.coinId,
          symbol: tokenData.symbol.toUpperCase(),
          display_name: tokenData.symbol,
        });

        existingToken = newToken;
        tokensAdded++;
      }

      // Add positions
      for (const pos of tokenData.positions) {
        await positionsHook.addPosition({
          tokenId: tokenData.coinId,
          type: pos.type,
          date: pos.date,
          quantity: pos.quantity,
          price: pos.price,
          note: pos.note,
        });
        positionsAdded++;
      }
    }

    // Reload tokens from storage
    const portfolio = PortfolioManager.getPortfolio(portfolioId);
    if (portfolio) {
      setTokens(portfolio.tokens);
    }

    toast(
      t('csvImport.success', '{{tokens}} tokens, {{positions}} positions imported', {
        tokens: tokensAdded,
        positions: positionsAdded
      }),
      'success'
    );

    logger.info('PortfolioDashboard', `CSV import: ${tokensAdded} tokens, ${positionsAdded} positions`);
  }, [tokens, portfolioHook, positionsHook, toast, t]);

  // Load demo portfolio with realistic sample data
  const handleLoadDemoPortfolio = useCallback(async () => {
    // Confirm if user already has data
    if (tokens.length > 0) {
      const confirmed = await confirm({
        title: t('portfolioDashboard.loadDemoTitle'),
        message: t('portfolioDashboard.demoLoadConfirm'),
        confirmText: t('common.confirm'),
        cancelText: t('common.cancel'),
        variant: 'warning'
      });
      if (!confirmed) return;
    }

    // Disable feature discovery tooltips when loading demo
    // (prevents flood of onboarding tooltips)
    featureDiscovery?.completeOnboarding();

    // Load demo data
    setTokens(DEMO_PORTFOLIO);

    // Save to localStorage via PortfolioManager
    const portfolioId = portfolioHook.activePortfolio?.id || 'default';
    PortfolioManager.updatePortfolioTokens(portfolioId, DEMO_PORTFOLIO);

    toast(`🎉 ${t('portfolioDashboard.demoLoaded', { count: DEMO_PORTFOLIO.length })}`, 'success');

    logger.info('PortfolioDashboard', `✅ Demo portfolio loaded with ${DEMO_PORTFOLIO.length} tokens`);
  }, [tokens.length, portfolioHook.activePortfolio?.id, toast, t, featureDiscovery, confirm]);

  // (ο) cleanup : `copyToClipboard` helper used to live here but was
  // never wired to the UI. Removed.

  // Keyboard shortcuts (Enhanced - Priority 1 Quick Win)
  useKeyboardShortcuts([
    // Navigation & Focus
    {
      key: 'f',
      ctrl: true,
      action: () => {
        const searchInput = document.querySelector('input[placeholder*="Rechercher"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      },
      description: 'Focus search'
    },
    {
      key: 'a',
      action: () => tokenInputRef.current?.focus(),
      description: 'Ajouter un token (focus input)'
    },
    {
      key: 'n',
      ctrl: true,
      action: () => modals.open('position'),
      description: 'Nouvelle position'
    },
    // Export/Import
    {
      key: 'e',
      ctrl: true,
      action: handleExportJSON,
      description: 'Exporter en JSON'
    },
    {
      key: 's',
      ctrl: true,
      action: handleExportJSON,
      description: 'Exporter backup JSON complet'
    },
    {
      key: 'i',
      ctrl: true,
      action: handleImportJSON,
      description: 'Importer JSON'
    },
    // View Toggles
    {
      key: 'c',
      action: () => setCompactMode(!compactMode),
      description: 'Toggle mode compact'
    },
    {
      key: 'b',
      ctrl: true,
      action: () => setShowBTCComparison(!showBTCComparison),
      description: 'Toggle comparaison BTC'
    },
    // Calculator Method Switching
    {
      key: '1',
      ctrl: true,
      action: () => {
        // This would require passing calculationMethod setter as prop
        // For now, just show toast
        toast(t('portfolioDashboard.methodFifoSelected'), 'info', 2000);
      },
      description: 'FIFO'
    },
    {
      key: '2',
      ctrl: true,
      action: () => {
        toast(t('portfolioDashboard.methodLifoSelected'), 'info', 2000);
      },
      description: 'LIFO'
    },
    {
      key: '3',
      ctrl: true,
      action: () => {
        toast(t('portfolioDashboard.methodAcbSelected'), 'info', 2000);
      },
      description: 'ACB'
    },
    // Help & Modals
    {
      key: '/',
      ctrl: true,
      action: () => setShowShortcutsHelp(!showShortcutsHelp),
      description: 'Afficher/masquer les raccourcis'
    },
    {
      key: 'h',
      shift: true,
      action: () => setShowShortcutsHelp(!showShortcutsHelp),
      description: 'Afficher/masquer les raccourcis'
    },
    {
      key: '?',
      action: () => setShowShortcutsHelp(!showShortcutsHelp),
      description: 'Afficher/masquer les raccourcis'
    },
    {
      key: 'Escape',
      action: () => {
        // Close all modals
        modals.close('position');
        modals.close('swap');
        modals.close('taxExport');
        setShowShortcutsHelp(false);
      },
      description: 'Fermer tous les modals'
    }
  ]);

  // Mobile swipe gestures (Left/Right are now handled by AppShell for sidebar control)
  // Note: SwipeUp/Down disabled to prevent conflicts with normal scrolling
  useSwipeGestures(dashboardRef, {
    // onSwipeDown: Disabled to prevent interference with normal scroll
    // onSwipeUp: Disabled to prevent interference with normal scroll
    onLongPress: () => {
      toast(`💡 ${t('portfolioDashboard.longPressTip')}`, 'info', 3000);
    },
    minSwipeDistance: 80,
    maxSwipeTime: 400
  });

  // Calculate quick stats for banner (MUST be before conditional returns to avoid hook order violation)
  const quickStats = useMemo(() => {
    if (tokens.length === 0) return null;

    // Find best performer (highest P/L %)
    let bestPerformer = { token: '', value: '' };
    let maxPLPct = -Infinity;

    // Find largest position (highest current value)
    let largestPosition = { token: '', value: '' };
    let maxValue = 0;

    // Count total positions
    let totalPositions = 0;

    tokens.forEach(token => {
      const result = fifoResults[token.id];
      if (!result) return;

      // Total positions count (use token.positions not result.positions which may be undefined)
      totalPositions += token.positions.length;

      // V2.G12.5: previously read `result.unrealizedPLPct` and
      // `result.currentValue` which don't exist on CalculatorResult — both
      // were `undefined`, so bestPerformer + largestPosition stayed empty
      // forever. Compute them properly from price + remainingQty + totalCost.
      const currentPrice = prices[token.id]?.usd || 0;
      const remainingQty = result.remainingQty || 0;
      const totalCost = result.totalCost || 0;
      const currentValue = currentPrice * remainingQty;
      const unrealizedPL = currentValue - totalCost;
      const unrealizedPLPct = totalCost > 0 ? (unrealizedPL / totalCost) * 100 : 0;

      // Best performer calculation
      if (unrealizedPLPct > maxPLPct) {
        maxPLPct = unrealizedPLPct;
        bestPerformer = {
          token: token.displayName.toUpperCase(),
          value: `${maxPLPct >= 0 ? '+' : ''}${fmt(maxPLPct, 2)}%`
        };
      }

      // Largest position calculation
      if (currentValue > maxValue) {
        maxValue = currentValue;
        largestPosition = {
          token: token.displayName.toUpperCase(),
          value: `$${fmt(currentValue, 0)}`
        };
      }
    });

    // Time since last update
    const timeSinceUpdate = pricesUpdatedAt
      ? Math.floor((Date.now() - pricesUpdatedAt) / 1000)
      : null;

    let lastUpdateText = 'Jamais';
    if (timeSinceUpdate !== null) {
      if (timeSinceUpdate < 60) {
        lastUpdateText = `il y a ${timeSinceUpdate}s`;
      } else if (timeSinceUpdate < 3600) {
        lastUpdateText = `il y a ${Math.floor(timeSinceUpdate / 60)}min`;
      } else {
        lastUpdateText = `il y a ${Math.floor(timeSinceUpdate / 3600)}h`;
      }
    }

    return {
      lastUpdate: lastUpdateText,
      bestPerformer,
      largestPosition,
      totalPositions
    };
  }, [tokens, fifoResults, prices, pricesUpdatedAt]);

  // Memoize dashboard grid widgets for performance (prevents rebuild on every render)
  const { filteredWidgets, widgetChildren } = useMemo(() => {
    const widgets: (WidgetConfig | undefined)[] = [];
    const children: React.ReactNode[] = [];

    // Helper to safely add widget and child together
    const addWidget = (id: string, child: React.ReactNode) => {
      const widget = ANALYTICS_WIDGETS.find(w => w.id === id);
      if (widget) {
        widgets.push(widget);
        children.push(child);
      }
    };

    // V2.G7.5: EntryPointsHeatmap moved to analytics/Trading (canonical
    // per V2.G1). Removing the duplicate from the dashboard. The widget
    // is still accessible at /analytics/trading.

    // Widget: Risk Analysis (always visible)
    addWidget('risk',
      <RiskAnalysis
        key="risk"
        tokens={tokens}
        prices={prices}
        fifoResults={fifoResults}
      />
    );

    // Widget: Price Chart (always visible)
    addWidget('price-chart',
      <Suspense key="price-chart" fallback={<ChartSkeleton />}>
        <TokenPriceChart
          tokens={tokens}
          prices={prices}
        />
      </Suspense>
    );

    // Widget: Performance vs BTC (only if BTC comparison enabled)
    if (showBTCComparison) {
      addWidget('spread',
        <CollapsibleSection
          key="spread"
          icon={Icons.Activity}
          title={t('portfolioDashboard.performanceVsBtc')}
          badge={{
            text: `${totalPLPct >= 0 ? '+' : ''}${fmt(totalPLPct, 2)}%`,
            color: totalPLPct >= 0 ? 'green' : 'red'
          }}
          defaultOpen={true}
        >
          <Suspense fallback={<ChartSkeleton />}>
            <SpreadChart
              tokens={tokens}
              isDark={isDark}
              days={90}
              prices={prices}
              btcHistoricalPrices={btcHistoricalPrices}
              btcCurrentPrice={btcCurrentPrice}
            />
          </Suspense>
        </CollapsibleSection>
      );
    }

    // V2.G7.5: DrawdownChart moved to analytics/Performance (canonical) ;
    // HoldingDurationAnalysis moved to analytics/Positions (canonical).
    // Both are still reachable for expert users via the Analytics hub.

    // Filter out any undefined widgets (safety check)
    return {
      filteredWidgets: widgets.filter((w): w is WidgetConfig => w !== undefined),
      widgetChildren: children
    };
  }, [
    tokens, prices, fifoResults, showBTCComparison, btcHistoricalPrices,
    btcCurrentPrice, userMode,
    totalPLPct, isDark, t
  ]);

  // Early returns AFTER all hooks
  if (loading) {
    return <PortfolioDashboardSkeleton />;
  }

  // If in 3D view mode, show the 3D universe
  if (viewMode === '3d') {
    return (
      <Suspense fallback={
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          gap: '20px',
          color: 'var(--text-primary)'
        }}>
          <div className="spinner" style={{ width: '40px', height: '40px' }} />
          <p style={{ fontSize: '16px', opacity: 0.7 }}>Chargement de la vue Univers 3D...</p>
        </div>
      }>
        <PortfolioUniverse3D
          positions={enrichedPositions}
          onClose={() => setViewMode('normal')}
        />
      </Suspense>
    );
  }

  return (
    <div ref={dashboardRef} style={{ minHeight: '100vh', width: '100%' }}>
      {/* V2.B2 — page chrome consistent with other V2 surfaces (MyAccount,
          Monitors, Visualizations*). Always visible, even on empty
          portfolio — uses the same primitives as the rest of the app.
          V2.H : hidden when V3 is active because the Hero card
          self-titles and the page title would be redundant.
          V2.I.4 : but only when V3 actually RENDERS — i.e. tokens.length > 0.
          On empty state V3 doesn't render, so we still need the page title. */}
      {(!useDashboardHeaderV3 || tokens.length === 0) && (
        <DataPageHeader
          icon={<Icons.Briefcase size={28} style={{ color: 'var(--primary)' }} />}
          title={t('portfolioDashboard.pageTitle', 'Mon Portfolio')}
          subtitle={t(
            'portfolioDashboard.pageSubtitle',
            'Suivez vos positions, votre P&L et vos statistiques en temps réel.',
          )}
        />
      )}

      {/* New Dashboard Header with Metrics, Charts and Suggestions.
          V2.G7: opt-in V2 layout behind `enable_dashboard_header_v2`.
          V2.H : opt-in V3 "Glass Hero" layout behind `enable_dashboard_header_v3`.
          V3 takes precedence over V2 when both flags are set. */}
      {tokens.length > 0 && (
        <Suspense fallback={<DashboardHeaderSkeleton />}>
          {useDashboardHeaderV3 ? (
            <DashboardHeaderV3
              tokens={tokens}
              prices={prices}
              fifoResults={fifoResults}
              currencySymbol={currencyInfo.symbol}
              totalValue={totalValue}
              totalCost={totalCost}
              totalPL={totalPL}
              totalPLPct={totalPLPct}
              totalRealizedPL={totalRealizedPL}
              totalUnrealizedPL={totalUnrealizedPL}
              portfolioVsBTC={portfolioVsBTC}
              showBTCComparison={showBTCComparison}
              btcCurrentPrice={btcCurrentPrice}
              priceSource={pricesCacheInfo}
              lastUpdate={quickStats?.lastUpdate ? new Date(quickStats.lastUpdate) : null}
              // V2.H.6 : click sur le donut → page analytics/Overview qui
              // a la distribution complète + treemap + sankey.
              onExpandDonut={() => onNavigate?.('analytics-overview')}
              onExpandSuggestions={() => onNavigate?.('analytics-insights')}
            />
          ) : useDashboardHeaderV2 ? (
            <DashboardHeaderV2
              tokens={tokens}
              prices={prices}
              fifoResults={fifoResults}
              currencySymbol={currencyInfo.symbol}
              totalValue={totalValue}
              totalCost={totalCost}
              totalPL={totalPL}
              totalPLPct={totalPLPct}
              totalRealizedPL={totalRealizedPL}
              totalUnrealizedPL={totalUnrealizedPL}
              portfolioVsBTC={portfolioVsBTC}
              showBTCComparison={showBTCComparison}
              btcCurrentPrice={btcCurrentPrice}
              priceSource={pricesCacheInfo}
              lastUpdate={quickStats?.lastUpdate ? new Date(quickStats.lastUpdate) : null}
            />
          ) : (
            <DashboardHeader
              tokens={tokens}
              prices={prices}
              fifoResults={fifoResults}
              currencySymbol={currencyInfo.symbol}
              totalValue={totalValue}
              totalCost={totalCost}
              totalPL={totalPL}
              totalPLPct={totalPLPct}
              totalRealizedPL={totalRealizedPL}
              totalUnrealizedPL={totalUnrealizedPL}
              portfolioVsBTC={portfolioVsBTC}
              showBTCComparison={showBTCComparison}
              btcCurrentPrice={btcCurrentPrice}
              priceSource={pricesCacheInfo}
              lastUpdate={quickStats?.lastUpdate ? new Date(quickStats.lastUpdate) : null}
            />
          )}
        </Suspense>
      )}

      <PortfolioSelectorBar
        activePortfolio={activePortfolio}
        onPortfolioChange={handlePortfolioChange}
        onCreatePortfolio={handleCreatePortfolio}
        onDeletePortfolio={(portfolio) => {
          toast(`✅ ${t('portfolioDashboard.portfolioDeleted')}: "${portfolio.name}"`, 'success');
          // Force a re-render by switching to the first remaining portfolio.
          const remaining = PortfolioManager.getAllPortfolios();
          if (remaining.length > 0 && activePortfolio?.id === portfolio.id) {
            handlePortfolioChange(remaining[0]);
          }
        }}
        onNavigate={onNavigate}
        pricesCacheInfo={pricesCacheInfo}
        tokenCount={tokens.length}
      />

      <PortfolioToolbarBar
        onOwlClick={() => onNavigate?.('crypto-quiz')}
        owlTitle={t('quiz.title', 'Quiz Crypto')}
        owlMessage={language === 'fr' ? '🎯 Testez-vous !' : '🎯 Test yourself!'}
        onAddToken={handleAddToken}
        coins={coins}
        tokensCount={tokens.length}
        filters={filters}
        onFiltersChange={setFilters}
        onFiltersReset={() => setFilters(DEFAULT_FILTERS)}
        onImportJSON={handleImportJSON}
        onImportCSV={() => modals.open('csvImport')}
        onExportCSV={handleExportCSV}
        onExportJSON={handleExportJSON}
        onTaxExport={() => modals.open('taxExport')}
        onLoadDemo={handleLoadDemoPortfolio}
        userMode={userMode}
        showBTCComparison={showBTCComparison}
        onToggleBTCComparison={() => setShowBTCComparison(!showBTCComparison)}
        onSnapshot={() => modals.open('snapshot')}
        onInvestmentSimulator={() => modals.open('investmentSimulator')}
      />

      {/* V2.B4.1 — saved filter views. Inline group rendered between
          toolbar and table; only shown when there are at least some
          tokens to filter or already-saved views to recall. */}
      {(tokens.length > 0 || savedViews.views.length > 0) && (
        <SavedFilterViews
          views={savedViews.views}
          hasActiveFilters={hasActiveFilters}
          onLoad={(viewId) => {
            const config = savedViews.loadView(viewId);
            if (config) setFilters(config);
          }}
          onSave={(name) => savedViews.saveView(name, filters)}
          onDelete={savedViews.deleteView}
        />
      )}

      {/* V2.J.3 — HBAR portfolio widget : surfaces a recent treasury
          release ONLY for users holding HBAR. Renders nothing
          otherwise. */}
      <HbarPortfolioWidget tokens={tokens} onNavigate={onNavigate} />

      {/* 5. Portfolio Table with collapse/expand positions */}
      <PortfolioTable
        tokens={filteredTokens}
        prices={prices}
        fifoResults={fifoResults}
        onAddPosition={handleAddPosition}
        onAddFirstToken={handleOpenAddTokenModal}
        onDeleteToken={handleDeleteToken}
        onEditPosition={handleEditPosition}
        onDeletePosition={handleDeletePosition}
        onSwapToken={(token) => {
          setSelectedToken(token);
          modals.open('swap');
        }}
        showBTCComparison={showBTCComparison}
        btcHistoricalPrices={btcHistoricalPrices}
        btcCurrentPrice={btcCurrentPrice}
        isNewToken={isNewToken}
        isExpertMode={userMode === 'expert'}
      />

      {/* ═══════════════════════════════════════════════════════════════════════════
          ANALYTICS GRID - Customizable drag & drop layout
          ═══════════════════════════════════════════════════════════════════════════ */}
      {tokens.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          {/* V2.B4 — preset selector + edit mode toggle on the same row.
              Selector left, toggle right. On mobile they wrap. */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              marginBottom: '16px',
            }}
          >
            <LayoutPresetSelector
              groupLabel={t('portfolioDashboard.layoutPresetGroup', 'Disposition du tableau de bord')}
              activePresetId={activePresetId}
              onSelect={handleSelectPreset}
              presets={[
                {
                  id: 'compact',
                  label: t('portfolioDashboard.presetCompact', 'Compact'),
                  description: t('portfolioDashboard.presetCompactDesc', 'Vue dense — 3 colonnes par ligne'),
                },
                {
                  id: 'standard',
                  label: t('portfolioDashboard.presetStandard', 'Standard'),
                  description: t('portfolioDashboard.presetStandardDesc', 'Disposition équilibrée par défaut'),
                },
                {
                  id: 'power',
                  label: t('portfolioDashboard.presetPower', 'Avancé'),
                  description: t('portfolioDashboard.presetPowerDesc', 'Pleine largeur — focus données'),
                },
              ]}
            />
            <EditModeToggle
              isEditMode={isEditMode}
              onToggle={() => setIsEditMode(!isEditMode)}
              onReset={() => {
                resetDashboardLayout(DASHBOARD_STORAGE_KEY, ANALYTICS_WIDGETS);
                StorageService.set(DASHBOARD_PRESET_STORAGE_KEY, 'standard');
                setActivePresetId('standard');
                setGridKey((k) => k + 1);
              }}
            />
          </div>

          {/* Customizable Dashboard Grid (memoized for performance).
              `key={gridKey}` forces a remount when the user selects a
              new preset → re-reads layout from localStorage. */}
          <DashboardGrid
            key={gridKey}
            widgets={filteredWidgets}
            storageKey={DASHBOARD_STORAGE_KEY}
            isEditMode={isEditMode}
          >
            {widgetChildren}
          </DashboardGrid>
        </div>
      )}

      {/* Position Modal */}
      <PositionModal
        isOpen={modals.isOpen('position')}
        onClose={() => {
          modals.close('position');
          setSelectedToken(null);
          setSelectedPosition(null);
        }}
        token={selectedToken || { id: '', displayName: '' }}
        position={selectedPosition}
        onSave={handleSavePosition}
        prices={prices}
      />

      {/* Swap Modal */}
      {selectedToken && (
        <SwapModal
          isOpen={modals.isOpen('swap')}
          onClose={() => {
            modals.close('swap');
            setSelectedToken(null);
          }}
          fromToken={selectedToken}
          tokens={tokens}
          prices={prices}
          onSwap={handleSwap}
          coins={coins}
        />
      )}

      {/* Add First Token Modal */}
      <AddTokenModal
        isOpen={modals.isOpen('addToken')}
        onClose={() => modals.close('addToken')}
        onAdd={handleAddToken}
        coins={coins}
      />

      {/* Keyboard Shortcuts Help Modal — touch-device guard lives in the
          component itself; the conditional here also keeps the React
          tree light when the panel is closed. */}
      <KeyboardShortcutsModal
        isOpen={showShortcutsHelp}
        onClose={() => setShowShortcutsHelp(false)}
        title={t('portfolioDashboard.keyboardShortcuts')}
        shortcuts={[
          { label: t('portfolioDashboard.addTokenFocusInput'), keys: 'A' },
          { label: t('portfolioDashboard.exportJsonShortcut'), keys: ['Ctrl', 'E'] },
          { label: t('portfolioDashboard.exportCsvShortcut'), keys: ['Ctrl', 'S'] },
          { label: t('portfolioDashboard.importJsonShortcut'), keys: ['Ctrl', 'I'] },
          { label: t('portfolioDashboard.toggleCompactMode'), keys: 'C' },
          { label: t('portfolioDashboard.toggleBtcComparison'), keys: 'B' },
          { label: t('portfolioDashboard.showShortcuts'), keys: [['Shift', 'H'], ['?']] },
        ]}
        footerNote={t('portfolioDashboard.shortcutsNotInInputs')}
      />

      {/* Portfolio Modal */}
      <PortfolioModal
        isOpen={modals.isOpen('portfolio')}
        onClose={() => modals.close('portfolio')}
        onSave={handleSavePortfolio}
        editPortfolio={null}
      />

      {/* Tax Export Modal */}
      <TaxExportModal
        isOpen={modals.isOpen('taxExport')}
        onClose={() => modals.close('taxExport')}
        tokens={tokens}
        prices={prices}
        fifoResults={fifoResults}
      />

      {/* Investment Simulator Modal */}
      <InvestmentSimulator
        isOpen={modals.isOpen('investmentSimulator')}
        onClose={() => modals.close('investmentSimulator')}
        tokens={tokens}
        prices={prices}
        fifoResults={fifoResults}
      />

      {/* CSV Import Modal */}
      <CSVImportModal
        isOpen={modals.isOpen('csvImport')}
        onClose={() => modals.close('csvImport')}
        onImport={handleCSVImport}
      />

      {/* Portfolio Snapshots Modal */}
      <SnapshotManager
        tokens={tokens}
        currentPrices={prices}
        isOpen={modals.isOpen('snapshot')}
        onClose={() => modals.close('snapshot')}
      />

      {/* PWA Install Prompt */}
      <PWAInstallPrompt />

      {/* Token Discovery Popup - Shows when HBAR/FLUX/CRO are detected */}
      {onNavigate && (
        <TokenDiscoveryPopup
          portfolioTokens={tokens.map(t => t.displayName).filter((s): s is string => Boolean(s))}
          onNavigate={onNavigate}
        />
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal {...confirmationProps} />

      {/* Investment Advisor AI Chat */}
      {tokens.length > 0 && (
        <InvestmentAdvisorChat
          tokens={tokens}
          prices={prices}
          fifoResults={fifoResults}
          calculationMethod={calculationMethod}
        />
      )}
    </div>
  );
}
